// Echte tiefenbegrenzte Bearbeitungen (Nut/Falz/Gehrung) via Manifold-CSG.
// Nur (ggf. rotierte) Quader-Subtraktionen — läuft schnell genug im Hauptthread; das WASM
// (~0,5 MB, /manifold.wasm aus public/) wird beim ersten Gebrauch initialisiert.
// Koordinaten wie die übrige Pipeline: Shape-Ebene (x = Länge, y = Breite,
// Welt-z = -Shape-y), Extrusion = Stärke, danach in den lokalen Teil-Frame
// gedreht (x, y = Stärke ±S/2, z = Breite).
import * as THREE from "three"
import { cutoutsOf, isThrough, isCircle } from "../cutouts"
import { outlinePolygon } from "../outline"
import { groovesOf, isFalz } from "../machinings"
import { mitersOf } from "../miters"

const CIRCLE_SEGMENTS = 32

let modulePromise

function manifoldModule() {
  modulePromise ??= import("manifold-3d")
    .then((factory) => factory.default({ locateFile: (file) => `/${file}` }))
    .then((wasm) => { wasm.setup(); return wasm })
  return modulePromise
}

export async function buildMachinedGeometry(part) {
  const { Manifold, CrossSection } = await manifoldModule()
  const trash = []
  const track = (object) => { trash.push(object); return object }

  try {
    const cross = track(new CrossSection([ shapeOutline(part), ...holePolygons(part) ], "Positive"))
    let solid = track(Manifold.extrude(cross, part.thickness_mm))
    solid = track(solid.rotate([ -90, 0, 0 ]))
    solid = track(solid.translate([ 0, -part.thickness_mm / 2, 0 ]))

    for (const box of grooveBoxes(part)) {
      const cutter = track(track(Manifold.cube(box.size, true)).translate(box.center))
      solid = track(solid.subtract(cutter))
    }

    for (const wedge of miterWedges(part)) {
      const cutter = track(track(track(Manifold.cube(wedge.size, true)).rotate(wedge.rotation)).translate(wedge.center))
      solid = track(solid.subtract(cutter))
    }

    const mesh = solid.getMesh()
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.BufferAttribute(mesh.vertProperties, 3))
    geometry.setIndex(new THREE.BufferAttribute(mesh.triVerts, 1))
    const flat = geometry.toNonIndexed() // getrennte Flächen für harte Kanten
    flat.computeVertexNormals()
    geometry.dispose()
    return flat
  } finally {
    trash.forEach((object) => object.delete())
  }
}

// Außenkontur CCW in Shape-Koordinaten: die engine-freie Kontur (teil-lokal
// [x, z], inkl. freier Kontur und Eckbearbeitung) mit Shape-y = -z kippen
function shapeOutline(part) {
  return outlinePolygon(part).map(([ x, z ]) => [ x, -z ])
}

// Durchbrüche als CW-Polygone (Löcher bei Fill-Rule "Positive")
function holePolygons(part) {
  return cutoutsOf(part).filter(isThrough).map((cutout) => {
    const cx = cutout.x_mm
    const cy = -cutout.z_mm
    if (isCircle(cutout)) {
      const radius = cutout.diameter_mm / 2
      return Array.from({ length: CIRCLE_SEGMENTS }, (_, i) => {
        const angle = -(i / CIRCLE_SEGMENTS) * Math.PI * 2
        return [ cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius ]
      })
    }
    const hl = cutout.length_mm / 2
    const hw = cutout.width_mm / 2
    return [ [ cx - hl, cy - hw ], [ cx - hl, cy + hw ], [ cx + hl, cy + hw ], [ cx + hl, cy - hw ] ]
  })
}

// Nut-/Falz-Quader im lokalen Teil-Frame (x = Länge, y = Stärke, z = Breite)
function grooveBoxes(part) {
  const { length_mm: l, thickness_mm: s, width_mm: b } = part
  return groovesOf(part).map((groove) => {
    const depth = groove.depth_mm
    const width = groove.width_mm
    const centerY = groove.side === "bottom" ? -s / 2 + (depth - 1) / 2 : s / 2 - (depth - 1) / 2
    const sizeY = depth + 1

    if (isFalz(groove)) {
      switch (groove.edge) {
        case "l1": return { size: [ l + 2, sizeY, width + 1 ], center: [ 0, centerY, -b / 2 + (width - 1) / 2 ] }
        case "l2": return { size: [ l + 2, sizeY, width + 1 ], center: [ 0, centerY, b / 2 - (width - 1) / 2 ] }
        case "b1": return { size: [ width + 1, sizeY, b + 2 ], center: [ -l / 2 + (width - 1) / 2, centerY, 0 ] }
        default: return { size: [ width + 1, sizeY, b + 2 ], center: [ l / 2 - (width - 1) / 2, centerY, 0 ] }
      }
    }

    if (groove.direction === "z") {
      // parallel zur Breite, offset ab linker Kante zur Nutmitte
      return { size: [ width, sizeY, b + 2 ], center: [ -l / 2 + groove.offset_mm, centerY, 0 ] }
    }
    // parallel zur Länge, offset ab vorderer Kante zur Nutmitte
    return { size: [ l + 2, sizeY, width ], center: [ 0, centerY, -b / 2 + groove.offset_mm ] }
  })
}

// Gehrungs-Keile (45° über die volle Stärke): ein großer Quader wird um 45° um
// die Kantenachse gedreht und so verschoben, dass seine (vormals obere) Fläche
// genau durch die obere Kantenlinie (y = +S/2) läuft und nach innen-unten
// abfällt — die Oberseite behält das volle Maß, die Unterseite weicht um S
// zurück. Subtraktion entfernt den außen liegenden Keil.
function miterWedges(part) {
  const { length_mm: l, thickness_mm: s, width_mm: b } = part
  const d = 4 * s + 8 // Kantenlänge des Schneidquaders: deckt die Fase (S·√2) sicher ab
  const off = (d * Math.SQRT2) / 4 // Abstand Quadermitte → Schnittfläche, in y- und Querrichtung
  const topY = s / 2 - off
  return mitersOf(part).map((miter) => {
    switch (miter.edge) {
      case "l1": return { size: [ l + 2, d, d ], rotation: [ 45, 0, 0 ], center: [ 0, topY, -b / 2 - off ] }
      case "l2": return { size: [ l + 2, d, d ], rotation: [ -45, 0, 0 ], center: [ 0, topY, b / 2 + off ] }
      case "b1": return { size: [ d, d, b + 2 ], rotation: [ 0, 0, -45 ], center: [ -l / 2 - off, topY, 0 ] }
      default: return { size: [ d, d, b + 2 ], rotation: [ 0, 0, 45 ], center: [ l / 2 + off, topY, 0 ] }
    }
  })
}
